/**
 * PersonaCard Component
 *
 * Card display for persona list items showing:
 * - Profile ID
 * - Generation mode badge
 * - Created timestamp
 * - Preview snippet
 * - View and copy buttons
 */

import { Link } from 'react-router-dom'
import { Copy, Eye, Clock, User } from 'lucide-react'
import { Persona, PersonaReference } from '@/hooks/usePersonas'
import { ModeBadge } from './PersonaModeSelector'

interface PersonaCardProps {
  persona: Persona | PersonaReference
  onCopy?: () => void
  showActions?: boolean
}

export default function PersonaCard({
  persona,
  onCopy,
  showActions = true,
}: PersonaCardProps) {
  const isFullPersona = 'communication_briefing' in persona

  const personaId = persona.persona_id
  const mode = persona.sensitivity_mode
  const generatedAt = 'generated_at' in persona ? persona.generated_at : undefined
  const productContext = persona.product_context

  // Get preview text
  const previewText = isFullPersona
    ? (persona as Persona).communication_briefing?.summary || 'No summary available'
    : `${productContext} persona - v${(persona as PersonaReference).current_version}`

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isFullPersona) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(persona, null, 2))
        onCopy?.()
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return 'Unknown'
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-personic-100 flex items-center justify-center">
            <User className="w-4 h-4 text-personic-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-mono">
              {personaId.slice(0, 8)}...
            </p>
            <p className="text-sm font-medium text-gray-900 capitalize">
              {productContext}
            </p>
          </div>
        </div>
        <ModeBadge mode={mode} />
      </div>

      {/* Preview */}
      <p className="text-sm text-gray-600 line-clamp-2 mb-3">{previewText}</p>

      {/* Timestamp */}
      <div className="flex items-center gap-1 text-xs text-gray-400 mb-4">
        <Clock className="w-3 h-3" />
        <span>{formatDate(generatedAt)}</span>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex items-center gap-2">
          <Link
            to={`/tenant/personas/${personaId}`}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-personic-50 text-personic-700 rounded-md text-sm font-medium hover:bg-personic-100 transition-colors"
          >
            <Eye className="w-4 h-4" />
            View
          </Link>
          {isFullPersona && (
            <button
              onClick={handleCopy}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
              title="Copy JSON"
            >
              <Copy className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Skeleton loader for PersonaCard
 */
export function PersonaCardSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gray-200" />
          <div>
            <div className="h-3 w-16 bg-gray-200 rounded mb-1" />
            <div className="h-4 w-20 bg-gray-200 rounded" />
          </div>
        </div>
        <div className="h-5 w-12 bg-gray-200 rounded" />
      </div>
      <div className="h-4 w-full bg-gray-200 rounded mb-2" />
      <div className="h-4 w-3/4 bg-gray-200 rounded mb-3" />
      <div className="h-3 w-24 bg-gray-200 rounded mb-4" />
      <div className="flex gap-2">
        <div className="flex-1 h-8 bg-gray-200 rounded" />
        <div className="w-10 h-8 bg-gray-200 rounded" />
      </div>
    </div>
  )
}
